import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApi, errMsg } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { DocumentUploads, REQUIRED_DOCS } from "@/components/DocumentUploads";

const TypeOption = ({ active, onClick, icon: Icon, title, text, id }) => (
  <button type="button" data-testid={id} onClick={onClick}
    className={`text-left p-4 rounded-xl border-2 transition-colors duration-200 ${active ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
    <Icon size={20} className={active ? "text-blue-600" : "text-slate-500"} />
    <p className="font-heading font-semibold mt-2">{title}</p>
    <p className="text-xs text-slate-500">{text}</p>
  </button>
);

export const ProfileForm = ({ onSaved }) => {
  const api = useApi();
  const { me, setMe } = useUser();
  const [f, setF] = useState({ display_name: "", account_type: "privato", company_name: "", city: "", phone: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (me) setF((p) => ({ ...p, display_name: me.display_name || "", account_type: me.account_type || "privato",
      company_name: me.company_name || "", city: me.city || "", phone: me.phone || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.user_id]);
  const isAdmin = me?.role === "admin";
  const submitting = !isAdmin && (me?.status !== "approved" || me?.account_type !== f.account_type);
  const missing = submitting && REQUIRED_DOCS[f.account_type].some((k) => !me?.documents?.[k]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api("put", "/me/profile", { ...f, company_name: f.account_type === "rivenditore" ? f.company_name : null });
      setMe(r);
      toast.success(r.status === "pending" ? "Richiesta inviata: attendi l'approvazione" : "Profilo salvato");
      onSaved?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4" data-testid="profile-form">
      <div className="grid grid-cols-2 gap-3">
        <TypeOption id="profile-type-privato" active={f.account_type === "privato"} onClick={() => setF({ ...f, account_type: "privato" })} icon={User} title="Privato" text="Acquisto per uso personale" />
        <TypeOption id="profile-type-rivenditore" active={f.account_type === "rivenditore"} onClick={() => setF({ ...f, account_type: "rivenditore" })} icon={Building2} title="Rivenditore" text="Concessionario o azienda" />
      </div>
      <div className="space-y-1.5"><Label>Nome e cognome</Label><Input data-testid="profile-name-input" required value={f.display_name} onChange={set("display_name")} /></div>
      {f.account_type === "rivenditore" && (
        <div className="space-y-1.5"><Label>Ragione sociale</Label><Input data-testid="profile-company-input" required value={f.company_name} onChange={set("company_name")} /></div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Città</Label><Input data-testid="profile-city-input" value={f.city} onChange={set("city")} /></div>
        <div className="space-y-1.5"><Label>Telefono</Label><Input data-testid="profile-phone-input" value={f.phone} onChange={set("phone")} /></div>
      </div>
      {!isAdmin && <DocumentUploads accountType={f.account_type} />}
      {missing && <p className="text-xs text-amber-700" data-testid="profile-missing-docs">Carica tutti i documenti richiesti per inviare la richiesta.</p>}
      <Button data-testid="profile-save-button" disabled={busy || missing} className="w-full rounded-full bg-slate-900 hover:bg-slate-800">
        {busy ? "Salvataggio..." : submitting ? "Invia richiesta di registrazione" : "Salva profilo"}
      </Button>
    </form>
  );
};
